import axios from "axios";
import { DISABLE_ALL_NETWORK, ApiConfig } from "./apiConfig";
import { getOrCreateDeviceId, persistDeviceIdFromServer } from "../../utils/deviceId";

const TIMEOUT = 30000;

const SESSION_CLEAR_CODES = new Set([
  "SESSION_CLEARED_IP",
  "SESSION_CLEARED_DEVICE",
  "DEVICE_BLOCKED",
  "SESSION_REVOKED",
  "SESSION_EXPIRED",
  "SESSION_MISSING",
]);

let refreshInFlight = null;

const networkDisabledResponse = () =>
  Promise.resolve({ success: false, message: null, data: null });

function readStorage(key) {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function authStorages() {
  const storages = [];
  try {
    if (localStorage.getItem("token") || localStorage.getItem("refresh_token")) {
      storages.push(localStorage);
    }
  } catch {
    // ignore
  }
  try {
    if (sessionStorage.getItem("token") || sessionStorage.getItem("refresh_token")) {
      storages.push(sessionStorage);
    }
  } catch {
    // ignore
  }
  if (!storages.length) storages.push(localStorage);
  return storages;
}

function writeTokenPair(access, refresh) {
  for (const s of authStorages()) {
    try {
      if (access) s.setItem("token", access);
      if (refresh) s.setItem("refresh_token", refresh);
    } catch {
      // ignore
    }
  }
  // Notify SocketContext / trade pages so WS handshake picks up the new JWT.
  try {
    window.dispatchEvent(new Event("agce-auth-changed"));
    window.dispatchEvent(new Event("agce-auth-token-changed"));
  } catch {
    // ignore (SSR / tests)
  }
}

function readJwtExpSeconds(token) {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const pad = "=".repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(atob(b64 + pad));
    const exp = Number(json?.exp);
    return Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

function isAccessExpiringSoon(token, skewSeconds = 60) {
  const exp = readJwtExpSeconds(token);
  if (exp == null) return false;
  return exp * 1000 <= Date.now() + skewSeconds * 1000;
}

function isRefreshUrl(url) {
  return /\/v1\/security\/auth\/refresh(\/|$|\?)/i.test(String(url || ""));
}

function clearSessionStorageAndReload(message) {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("userId");
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("userId");
  } catch {
    // ignore
  }
  try {
    if (message) sessionStorage.setItem("agce.auth.clear_reason", String(message));
  } catch {
    // ignore
  }
  const path = typeof window !== "undefined" ? window.location?.pathname || "" : "";
  if (path === "/login" || path === "/auth/verify") {
    return;
  }
  if (typeof window !== "undefined") {
    window.location.href = "/login";
  }
}

function getAccessTokenRaw() {
  const raw = readStorage("token");
  if (!raw) return null;
  return String(raw).replace(/^Bearer\s+/i, "").trim() || null;
}

function getRefreshTokenRaw() {
  const raw = readStorage("refresh_token");
  return raw ? String(raw).trim() : null;
}

function isFormDataBody(body) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

/** Merge Authorization (if available) + X-Device-Id onto every request. */
export function mergeDefaultHeaders(headers = {}, accessOverride = undefined, body = undefined) {
  const h = { ...(headers || {}) };
  const lower = Object.keys(h).reduce((acc, k) => {
    acc[k.toLowerCase()] = k;
    return acc;
  }, {});

  // A JSON content type makes axios serialize FormData and drop the file, so leave it
  // unset and let the browser add multipart boundaries.
  if (!lower["content-type"] && !isFormDataBody(body)) {
    h["Content-Type"] = "application/json";
  }
  if (!lower["x-device-id"]) {
    h["X-Device-Id"] = getOrCreateDeviceId();
  }

  const access = accessOverride !== undefined ? accessOverride : getAccessTokenRaw();
  if (access) {
    if (lower.authorization) delete h[lower.authorization];
    h.Authorization = `Bearer ${access}`;
  } else if (lower.authorization) {
    const key = lower.authorization;
    const v = String(h[key] || "");
    if (v && !/^Bearer\s+/i.test(v)) {
      h[key] = `Bearer ${v}`;
    }
  }
  return h;
}

function refreshRequestHeaders() {
  return {
    "Content-Type": "application/json",
    "X-Device-Id": getOrCreateDeviceId(),
  };
}

async function tryRefreshAccessToken() {
  const refresh = getRefreshTokenRaw();
  if (!refresh) return null;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const url = `${ApiConfig.baseSecurity}auth/refresh`;
      const res = await axios.post(
        url,
        { refresh_token: refresh },
        {
          headers: refreshRequestHeaders(),
          timeout: TIMEOUT,
        }
      );
      const body = res?.data || {};
      if (body.success === false) return null;
      const d = body.data && typeof body.data === "object" ? body.data : body;
      const access = d.access_token || d.token;
      const nextRefresh = d.refresh_token || refresh;
      if (!access) return null;
      const nextAccess = String(access).replace(/^Bearer\s+/i, "");
      writeTokenPair(nextAccess, nextRefresh);
      if (d.device_id) persistDeviceIdFromServer(d.device_id);
      return nextAccess;
    } catch (e) {
      const code = e?.response?.data?.code;
      if (SESSION_CLEAR_CODES.has(code) || code === "REFRESH_INVALID" || code === "REFRESH_REUSE") {
        clearSessionStorageAndReload(e?.response?.data?.message || "Session ended. Please log in again.");
      }
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function ensureFreshAccessToken() {
  const refresh = getRefreshTokenRaw();
  const access = getAccessTokenRaw();
  if (!refresh) return access;
  if (!access || isAccessExpiringSoon(access)) {
    return (await tryRefreshAccessToken()) || access;
  }
  return access;
}

const handleApiError = async (error, retryFn) => {
  if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
    return { success: false, message: "Request timeout. Please try again." };
  }

  if (!error.response) {
    return { success: false, message: "Network error. Please check your connection." };
  }

  const payload = error?.response?.data || {};
  const code = payload.code;
  const message = payload.message;

  if (SESSION_CLEAR_CODES.has(code)) {
    clearSessionStorageAndReload(message || "Session ended. Please log in again.");
    return payload;
  }

  const expired =
    code === "TOKEN_EXPIRED" ||
    message === "Token is expired" ||
    message === "Token is Expired";

  if (expired && typeof retryFn === "function") {
    const newAccess = await tryRefreshAccessToken();
    if (newAccess) {
      try {
        return await retryFn(newAccess);
      } catch (retryErr) {
        return retryErr?.response?.data || { success: false, message: "Request failed after refresh." };
      }
    }
    clearSessionStorageAndReload(message || "Token is expired");
    return payload;
  }

  if (expired) {
    clearSessionStorageAndReload(message || "Token is expired");
    return payload;
  }

  return payload;
};

export const ApiCallPost = async (url, parameters, headers) => {
  if (DISABLE_ALL_NETWORK) return networkDisabledResponse();
  const skipAuth = isRefreshUrl(url);
  const access = skipAuth ? null : await ensureFreshAccessToken();
  const merged = skipAuth
    ? { ...refreshRequestHeaders(), ...(headers || {}) }
    : mergeDefaultHeaders(headers, access, parameters);
  try {
    const response = await axios.post(url, parameters, { headers: merged, timeout: TIMEOUT });
    return response?.data;
  } catch (error) {
    return handleApiError(error, async (newAccess) => {
      const h = { ...merged, Authorization: `Bearer ${newAccess}` };
      const response = await axios.post(url, parameters, { headers: h, timeout: TIMEOUT });
      return response?.data;
    });
  }
};

export const ApiCallGet = async (url, headers) => {
  if (DISABLE_ALL_NETWORK) return networkDisabledResponse();
  const access = await ensureFreshAccessToken();
  const merged = mergeDefaultHeaders(headers, access);
  try {
    const response = await axios.get(url, { headers: merged, timeout: TIMEOUT });
    return response?.data;
  } catch (error) {
    return handleApiError(error, async (newAccess) => {
      const h = { ...merged, Authorization: `Bearer ${newAccess}` };
      const response = await axios.get(url, { headers: h, timeout: TIMEOUT });
      return response?.data;
    });
  }
};

export const ApiCallGetVerifyRegistration = async (url, headers) => {
  if (DISABLE_ALL_NETWORK) return networkDisabledResponse();
  const merged = mergeDefaultHeaders(headers);
  try {
    const response = await axios.get(url, { headers: merged, timeout: TIMEOUT });
    return response?.data;
  } catch (error) {
    return error?.response?.data;
  }
};

export const ApiCallPut = async (url, parameters, headers) => {
  if (DISABLE_ALL_NETWORK) return networkDisabledResponse();
  const access = await ensureFreshAccessToken();
  const merged = mergeDefaultHeaders(headers, access, parameters);
  try {
    const response = await axios.put(url, parameters, { headers: merged, timeout: TIMEOUT });
    return response?.data;
  } catch (error) {
    return handleApiError(error, async (newAccess) => {
      const h = { ...merged, Authorization: `Bearer ${newAccess}` };
      const response = await axios.put(url, parameters, { headers: h, timeout: TIMEOUT });
      return response?.data;
    });
  }
};

export const ApiCallPatch = async (url, parameters, headers) => {
  if (DISABLE_ALL_NETWORK) return networkDisabledResponse();
  const access = await ensureFreshAccessToken();
  const merged = mergeDefaultHeaders(headers, access, parameters);
  try {
    const response = await axios.patch(url, parameters, { headers: merged, timeout: TIMEOUT });
    return response?.data;
  } catch (error) {
    return handleApiError(error, async (newAccess) => {
      const h = { ...merged, Authorization: `Bearer ${newAccess}` };
      const response = await axios.patch(url, parameters, { headers: h, timeout: TIMEOUT });
      return response?.data;
    });
  }
};

export const ApiCallDelete = async (url, headers, parameters) => {
  if (DISABLE_ALL_NETWORK) return networkDisabledResponse();
  const access = await ensureFreshAccessToken();
  const merged = mergeDefaultHeaders(headers, access);
  try {
    const response = await axios.delete(url, {
      headers: merged,
      timeout: TIMEOUT,
      ...(parameters !== undefined ? { data: parameters } : {}),
    });
    return response?.data;
  } catch (error) {
    return handleApiError(error, async (newAccess) => {
      const h = { ...merged, Authorization: `Bearer ${newAccess}` };
      const response = await axios.delete(url, {
        headers: h,
        timeout: TIMEOUT,
        ...(parameters !== undefined ? { data: parameters } : {}),
      });
      return response?.data;
    });
  }
};
